import { axiosClient } from '../../../../library/Service/axiosClient';
import pageConfig from '../internal-admin/PageConfigGrievanceInternal.json';

// API base — internal admin grievance APIs served from school-management-service
const BASE = process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER;

export interface GrievanceTag {
    tagCode: string;
    tagLabel: string;
    tagType: 'preset' | 'system' | 'custom';
}

/** Preset tag as returned by GET /grievance/preset-tags */
export interface PresetTag {
    tagCode: string;
    tagLabel: string;
    tagType: 'preset';
    icon: string;        // Bootstrap icon class, e.g. 'bi-circle-fill'
    colorClass: string;  // Bootstrap color, e.g. 'success', 'danger', 'warning'
}

/** System-defined tag as returned by GET /grievance/:id/system-tags */
export interface SystemTag {
    tagCode: string;
    tagLabel: string;
    tagType: 'system';
    icon?: string | null;
    colorClass?: string | null;
}

export interface GrievanceAlumni {
    uuid: string | null;
    name: string;
    tier: string | null;
    program: string | null;
    batch: string | null;
    avatarUrl: string | null;
    genderCode: string | null;
}

export interface GrievancePushRecord {
    id: number;
    institutePriority: string | null;
    teamNotes: string;
    suggestedResolution: string;
    tags: GrievanceTag[];
}

export interface InternalGrievanceCard {
    id: number;
    code: string;
    priority: string;
    status: string;
    category: string;
    isInternalOnly: boolean;
    receivedDate: string;
    updatedAt: string;
    alumni: GrievanceAlumni;
    push: GrievancePushRecord | null;
}

export interface TabCounts {
    total: number;
    pendingReview: number;
    pushedToInstitute: number;
    internalOnly: number;
}

export const defaultSearchCriteria = () => ({
    search: '',
    instituteId: '',
    priority: '',
    status: '',
    category: '',
    createdFrom: '',
    createdTo: '',
    updatedFrom: '',
    updatedTo: '',
    tab: 'total',
});

export type SearchCriteria = ReturnType<typeof defaultSearchCriteria>;

class InternalAdminGrievanceService {
    // React Router pageLoader — returns SmartContext-compatible state shape
    static async pageLoader(params: any): Promise<any> {
        const searchCriteria = defaultSearchCriteria();
        const response = await InternalAdminGrievanceService.search(searchCriteria);

        return {
            formConfig: pageConfig,
            data: {
                searchCriteria,
                grievanceList: response.grievances ?? [],
                tabCounts: response.tabCounts ?? { total: 0, pendingReview: 0, pushedToInstitute: 0, internalOnly: 0 },
            },
            routeInfo: { pageName: 'internal-admin-grievance-v2', ...params },
            internal: { gridState: {} },
        };
    }

    // POST /api/v1/grievance/internal-list — returns { grievances, tabCounts }
    static async search(criteria: SearchCriteria): Promise<{ grievances: InternalGrievanceCard[]; tabCounts: TabCounts }> {
        const response = await axiosClient().post(`${BASE}/grievance/internal-list`, criteria);
        return response.data?.data ?? {
            grievances: [],
            tabCounts: { total: 0, pendingReview: 0, pushedToInstitute: 0, internalOnly: 0 },
        };
    }

    // POST /api/v1/grievance/:id/push — create push (UAS-2244 integration point)
    static async createPush(id: number, payload: object): Promise<{ success: boolean; pushId: number }> {
        const response = await axiosClient().post(`${BASE}/grievance/${id}/push`, payload);
        return response.data;
    }

    // PUT /api/v1/grievance/:id/push — update push (UAS-2244 integration point)
    static async updatePush(id: number, payload: object): Promise<{ success: boolean; pushId: number }> {
        const response = await axiosClient().put(`${BASE}/grievance/${id}/push`, payload);
        return response.data;
    }

    // DELETE /api/v1/grievance/:id/push — undo push (AC14)
    static async undoPush(id: number): Promise<{ success: boolean; message: string }> {
        const response = await axiosClient().delete(`${BASE}/grievance/${id}/push`);
        return response.data;
    }

    // GET /api/v1/grievance/preset-tags — backend-driven preset tag list for Push modal
    static async getPresetTags(): Promise<PresetTag[]> {
        const response = await axiosClient().get(`${BASE}/grievance/preset-tags`);
        return response.data?.data ?? [];
    }

    // GET /api/v1/grievance/:id/system-tags — qualifying system tags for a specific grievance
    static async getSystemTags(grievanceId: number): Promise<SystemTag[]> {
        const response = await axiosClient().get(`${BASE}/grievance/${grievanceId}/system-tags`);
        return response.data?.data ?? [];
    }

    // AC15: Export currently shown grievances as CSV (client-side)
    static exportCSV(grievances: InternalGrievanceCard[]): void {
        const headers = [
            'Grievance ID', 'Alumni Name', 'Program', 'Batch', 'Category',
            'Visibility', 'Priority', 'Status', 'Received Date', 'Last Updated',
            'Pushed to Institute', 'Institute Priority',
        ];

        const rows = grievances.map((g) => [
            g.code,
            g.alumni.name,
            g.alumni.program ?? '',
            g.alumni.batch ?? '',
            g.category,
            g.isInternalOnly ? 'Internal Only' : 'Always to Institute',
            g.priority,
            g.status,
            g.receivedDate,
            g.updatedAt,
            g.push ? 'Yes' : 'No',
            g.push?.institutePriority ?? '',
        ]);

        const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
        const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
        const filename = `grievances_export_${today}.csv`;

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}

export default InternalAdminGrievanceService;
